// RAFINAREA CU MODEL — panoul de depanare al pasului `rafinare_llm` (arhitectura,
// după curățare, pe câștigătoarea subsolului). Arată, pe nivel: cifrele (locuri
// înainte → după, loturi acceptate / respinse, pași, secunde, cost, verificatorul)
// și JURNALUL a tot ce a cerut modelul, în ordine: fiecare interogare cu textul
// și uneltele ei, fiecare variantă evaluată descrisă în cuvinte cu bilanțul ei,
// fiecare lot aplicat sau respins cu motivul și, la respingere, regula încălcată.
// Aceeași descriere a operațiilor ca în `rafinare_llm/raport.py`.
import { useState } from "react";

type Op = Record<string, unknown> & { op?: string; ids?: string[] };
type RezultatLot = {
  acceptat?: boolean; diferenta?: number; sterse_automat?: string[] | number;
  incalcari_noi?: Record<string, unknown[]>; pasi?: string[]; locuri?: number;
};
type Intrare =
  | { fel: "pas"; trecere: number; pas: number; secunde?: number; secunde_unelte?: number;
      text?: string[]; unelte?: { nume: string; input: unknown }[] }
  | { fel: "lot"; tip: "acceptat" | "respins" | "evaluat" | "anulat" | string; motiv?: string;
      operatii: unknown; rezultat: unknown };
export type NivelRafinare = {
  nivel: number; locuri_inainte: number; locuri_dupa: number;
  loturi?: { acceptate: number; respinse: number }; pasi?: number;
  secunde_model?: number; secunde_unelte?: number; cost_estimat_usd?: number | null; t_s?: number;
  treceri?: { trecere: number; inainte: number; dupa: number; rezumat: string; secunde?: number }[];
  incalcari_la_pornire?: Record<string, number>;
  verificator?: { compliance_ok?: boolean; compliance?: { id: string; ok: boolean; detail: string }[] };
  jurnal?: Intrare[];
};
export type RaportRafinare = {
  status: string; motiv?: string; model?: string | null; t_s?: number;
  locuri_inainte?: number; locuri_dupa?: number; cost_estimat_usd?: number | null;
  niveluri?: NivelRafinare[];
  curatare_dupa?: { status?: string; adaugate?: Record<string, number>; note?: string[]; t_s?: number };
};

const m = (x: unknown) => (typeof x === "number" ? x.toFixed(2).replace(".", ",") : String(x));

/** O operație a modelului, în cuvinte (oglinda lui `raport.descrie`). */
export function descrie(o: Op): string {
  const ids = (o.ids ?? []).join(", ");
  switch (o.op) {
    case "muta_cu_randuri": return `mută ${ids} cu rândurile lor cu (${m(o.du ?? 0)}, ${m(o.dv ?? 0)}) m`;
    case "muta": return `mută ${ids} cu (${m(o.du ?? 0)}, ${m(o.dv ?? 0)}) m`;
    case "alee": {
      const cap = `de la (${m(o.u1)}, ${m(o.v1)}) la (${m(o.u2)}, ${m(o.v2)})`;
      return o.id ? `redefinește aleea ${String(o.id)} ${cap}` : `alee nouă ${cap}, lățime ${m(o.w ?? 6)}`;
    }
    case "sterge_alei": return `șterge aleile ${ids}`;
    case "sterge_locuri": {
      const l = o.ids ?? [];
      return `șterge locurile ${l.slice(0, 12).join(", ")}${l.length > 12 ? "…" : ""}`;
    }
    case "sterge_zona": return `șterge locurile din zona u ${m(o.x0)}–${m(o.x1)}, v ${m(o.y0)}–${m(o.y1)}`;
    case "adauga_rand":
      return `rând ${String(o.tip ?? "perp")} pe axa ${String(o.axa ?? "u")} între ${m(o.de_la)} și ${m(o.pana_la)}, `
        + `linia ${m(o.linie)} spre ${String(o.spre ?? "+")}`;
    case "adauga_loc":
      return `loc ${String(o.tip ?? "perp")} la (${m(o.u)}, ${m(o.v)}), orientare ${String(o.orientare ?? "v")}`;
    default: return JSON.stringify(o);
  }
}

function Bilant({ r }: { r: RezultatLot }) {
  if (r.acceptat) {
    const n = typeof r.sterse_automat === "number" ? r.sterse_automat : (r.sterse_automat?.length ?? 0);
    return <b style={{ color: (r.diferenta ?? 0) > 0 ? "var(--ok, #060)" : (r.diferenta ?? 0) < 0 ? "var(--bad, #a00)" : undefined }}>
      acceptat, {(r.diferenta ?? 0) > 0 ? "+" : ""}{r.diferenta ?? 0} locuri
      {n ? ` (${n} șterse automat, rândurile refăcute)` : ""}
    </b>;
  }
  const inc = r.incalcari_noi ?? {};
  return <span><b style={{ color: "var(--bad, #a00)" }}>respins</b>: {
    Object.entries(inc).map(([k, v]) => `${k}: ${v.slice(0, 6).map(String).join(", ")}`).join("; ")
  }</span>;
}

function Lot({ e }: { e: Extract<Intrare, { fel: "lot" }> }) {
  if (e.tip === "evaluat") {
    const variante = (e.operatii as Op[][]) ?? [];
    const rez = (e.rezultat as RezultatLot[]) ?? [];
    return (
      <div className="ob-raf-ev">
        <b>evalueaza</b> · {variante.length} variante, fără să aplice
        <ol className="ob-raf-variante">
          {variante.map((ops, i) => (
            <li key={i}>
              {ops.map((o, j) => <div key={j}>{descrie(o)}</div>)}
              <div>{rez[i] ? <Bilant r={rez[i]} /> : null}</div>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (e.tip === "anulat") {
    return <div className="ob-raf-lot"><b>anuleaza</b> ultimul lot → {(e.rezultat as RezultatLot)?.locuri} locuri</div>;
  }
  const ops = (e.operatii as Op[]) ?? [];
  const r = (e.rezultat as RezultatLot) ?? {};
  const auto = (r.pasi ?? []).filter((p) => p.includes("automat"));
  return (
    <div className={`ob-raf-lot${e.tip === "respins" ? " respins" : ""}`}>
      <b>aplica</b>{e.motiv ? <> — <i>{e.motiv}</i></> : null}
      {ops.map((o, j) => <div key={j}>{descrie(o)}</div>)}
      <div><Bilant r={r} /></div>
      {auto.length ? <div className="sg-muted">codul, după lot: {auto.join("; ")}</div> : null}
    </div>
  );
}

function Jurnal({ jurnal }: { jurnal: Intrare[] }) {
  return (
    <div className="ob-raf-jurnal">
      {jurnal.map((e, i) => e.fel === "pas" ? (
        <div key={i} className="ob-raf-pas">
          <b>pasul {e.trecere}.{e.pas}</b>
          <span className="sg-muted"> · model {e.secunde ?? "—"} s{e.secunde_unelte != null ? ` + unelte ${e.secunde_unelte} s` : ""}
            {" · "}{(e.unelte ?? []).map((u) => u.nume).join(", ") || "fără unealtă"}</span>
          {(e.text ?? []).map((t, j) => <p key={j} className="ob-raf-text">{t}</p>)}
          {(e.unelte ?? []).filter((u) => u.nume === "vezi" || u.nume === "gata").map((u, j) => {
            const inp = (typeof u.input === "string" ? JSON.parse(u.input) : u.input) as Record<string, unknown>;
            return <div key={j} className="sg-muted">
              {u.nume === "vezi"
                ? `vezi zona u ${m(inp.u0)}–${m(inp.u1)}, v ${m(inp.v0)}–${m(inp.v1)}`
                : `gata: ${String(inp.rezumat ?? "")}`}
            </div>;
          })}
        </div>
      ) : <Lot key={i} e={e} />)}
    </div>
  );
}

function Nivel({ n }: { n: NivelRafinare }) {
  const [deschis, setDeschis] = useState(false);
  const delta = n.locuri_dupa - n.locuri_inainte;
  const pica = (n.verificator?.compliance ?? []).filter((r) => !r.ok);
  const porn = Object.entries(n.incalcari_la_pornire ?? {});
  return (
    <div className="ob-raf-nivel">
      <div>
        <b>nivelul {n.nivel}</b>: {n.locuri_inainte} → {n.locuri_dupa}
        {" "}<b style={{ color: delta > 0 ? "var(--ok, #060)" : delta < 0 ? "var(--bad, #a00)" : undefined }}>
          ({delta > 0 ? "+" : ""}{delta})</b>
        {" · "}loturi {n.loturi?.acceptate ?? 0} / {n.loturi?.respinse ?? 0} respinse
        {" · "}{n.pasi ?? 0} pași, model {Math.round(n.secunde_model ?? 0)} s, unelte {Math.round(n.secunde_unelte ?? 0)} s
        {n.cost_estimat_usd != null ? ` · ~${n.cost_estimat_usd} $` : ""}
      </div>
      <div className="sg-muted">
        verificator: {pica.length ? pica.map((r) => `${r.id} (${r.detail})`).join(", ") : "ok"}
        {porn.length ? ` · la pornire: ${porn.map(([k, v]) => `${k} ${v}`).join(", ")}` : ""}
      </div>
      {(n.treceri ?? []).map((t) => (
        <div key={t.trecere} className="ob-raf-trecere">
          <b>trecerea {t.trecere}</b>: {t.inainte} → {t.dupa}{t.secunde != null ? ` în ${Math.round(t.secunde)} s` : ""}
          <div className="sg-muted">{t.rezumat}</div>
        </div>
      ))}
      <button className="ob-raf-toggle" onClick={() => setDeschis((d) => !d)}>
        {deschis ? "ascunde jurnalul" : `jurnalul modelului · ${(n.jurnal ?? []).length} intrări`}
      </button>
      {deschis && n.jurnal ? <Jurnal jurnal={n.jurnal} /> : null}
    </div>
  );
}

export default function RafinareDepanare({ raport }: { raport: RaportRafinare | null }) {
  if (!raport) return <p className="sg-muted">Fără rafinare cu model în document (rulare dinainte de 14.10.2026).</p>;
  if (raport.status !== "ok") {
    return <p className="sg-muted">
      {raport.status === "nerulat" ? "Nu a rulat" : "A picat"}: {raport.motiv ?? "—"}
    </p>;
  }
  const cur = raport.curatare_dupa;
  return (
    <div className="ob-raf">
      <div>
        {raport.model ?? "model"} · {raport.locuri_inainte} → {raport.locuri_dupa} locuri
        {" · "}{raport.t_s != null ? `${(raport.t_s / 60).toFixed(1)} min` : ""}
        {raport.cost_estimat_usd != null ? ` · ~${raport.cost_estimat_usd} $` : ""}
      </div>
      {(raport.niveluri ?? []).map((n) => <Nivel key={n.nivel} n={n} />)}
      {cur ? (
        <div className="sg-muted">
          curățarea de după model ({cur.status ?? "—"}
          {cur.adaugate ? `, ${Object.entries(cur.adaugate).map(([k, v]) => `${k}: ${v > 0 ? "+" : ""}${v}`).join(", ")}` : ""}
          ): {(cur.note ?? []).join("; ") || "nimic de schimbat"}
        </div>
      ) : null}
    </div>
  );
}
